import LazyImage from 'components/Image/LazyImage';
import { FrameImage } from 'components/Product/ProductImagesModal';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { CartItem, Product } from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import Money from 'components/Money/Money';
import noImage from '../icons/noIMG.svg';
import NoteIcon from '../icons/noteIcon';

const ProductItemWrapper = styled.div`
  background: #fff;
  padding: 0px 16px;
`;
const ProductItemContainer = styled.div`
  padding: 12px 0px;
  background: #fff;
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #eaeaeb;

  ${({ isLastItem }) =>
    isLastItem &&
    `
    border-bottom: none;
    `}
`;

const ProductItemImage = styled.div<any>`
  position: relative;
  overflow: hidden;
  background: #ffffff;
  border-radius: 4px;
  display: inline-block;
  height: 32px;
  width: 32px;
`;

const ProductInfoDetailContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-self: stretch;
`;

const ProductInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: space-between;
`;

const ProductName = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #111827;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`;

const ProductQuantity = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #4b5563;
  font-weight: 500;
`;

const ProductUom = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #4b5563;
  font-weight: 400;
`;

const ProductPrice = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #111827;
  font-weight: 600;
`;

const ProductNote = styled.div`
  word-break: normal;
  overflow-wrap: break-word;
  font-size: 12px;
  line-height: 16px;
  color: #757d73;
  font-weight: 500;
  max-width: 80%;
`;

const ProductNoteContainer = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  cursor: pointer;
`;

const NoteIconWrapper = styled.div`
  flex-shrink: 0;
  margin-top: 3px;
`;

const ItemImage = styled(({ noImage, ...rest }) => <LazyImage {...rest} />)<any>`
  width: 32px;
  height: 32px;
  object-fit: ${({ noImage }) => (noImage ? 'contain' : 'cover')};
  ${({ noImage }) => (noImage ? `opacity: 0.3;` : '')};
`;

interface ConfirmedCartItemMobileProps {
  cartItem: CartItem;
  lang: string;
  itemDiscountBeforeVat: number;
  isLastItem: boolean;
}

const ConfirmedCartItemMobile = (props: ConfirmedCartItemMobileProps) => {
  const { cartItem, lang, itemDiscountBeforeVat, isLastItem } = props;

  const { uomInfo } = useProductUomInfo(cartItem?.product as Product);

  const frameTagImage = lang === 'vi' ? cartItem?.product?.tagImageVn : cartItem?.product?.tagImageEn || '';

  const hasNote = useMemo(() => {
    return !!cartItem?.product?.note;
  }, [cartItem?.product?.note]);

  const totalAfterVat = useMemo(() => {
    const totalBeforeVAT =
      calculateVAT(
        cartItem?.product?.priceV2?.price || 0,
        cartItem?.product?.priceV2?.vat || 0,
        cartItem?.product?.priceV2?.vatIncluded || false,
      ).beforeVat * (cartItem?.quantity || 0);

    const totalAfterDiscountExcludedVAT = totalBeforeVAT - itemDiscountBeforeVat;

    const totalVATValue = Math.round((totalAfterDiscountExcludedVAT * (cartItem?.product?.priceV2?.vat || 0)) / 100);

    return totalAfterDiscountExcludedVAT + totalVATValue;
  }, [itemDiscountBeforeVat, cartItem]);

  return (
    <ProductItemWrapper>
      <ProductItemContainer isLastItem={isLastItem}>
        <ProductItemImage>
          <ItemImage
            noImage={!cartItem?.product?.imageUrl}
            src={cartItem?.product?.imageUrl || noImage}
            alt={cartItem?.product?.name || name}
          />
          {frameTagImage && <FrameImage src={frameTagImage} />}
        </ProductItemImage>
        <ProductInfoDetailContainer>
          <ProductInfoItem>
            <ProductName>{cartItem?.product?.name}</ProductName>
            <ProductQuantity>x{cartItem?.quantity}</ProductQuantity>
          </ProductInfoItem>
          <ProductInfoItem>
            <ProductUom>{uomInfo}</ProductUom>
            <ProductPrice>
              <Money>{totalAfterVat}</Money>
            </ProductPrice>
          </ProductInfoItem>
          {hasNote && (
            <ProductNoteContainer>
              <NoteIconWrapper>
                <NoteIcon size={10} color="#6B7280" />
              </NoteIconWrapper>
              <ProductNote>{cartItem?.product?.note}</ProductNote>
            </ProductNoteContainer>
          )}
        </ProductInfoDetailContainer>
      </ProductItemContainer>
    </ProductItemWrapper>
  );
};

export default ConfirmedCartItemMobile;
